import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { interval, Subject } from 'rxjs';
import { switchMap, takeWhile, takeUntil, startWith } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import {
  CameraDto,
  MasterDeviceAssignmentDto,
  CameraEventFetchDto,
  CameraEventFetchStatus,
  FetchCameraEventsRequest
} from '../../../../../../../api/gen';
import { CameraEventService } from '../../../../../../../api/gen/api/cameraEvent.service';
import { MasterDeviceAssignmentService } from '../../../../../../../api/gen/api/masterDeviceAssignment.service';

@Component({
  selector: 'app-camera-event-fetch-action',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatChipsModule
  ],
  templateUrl: './camera-event-fetch-action.component.html',
  styleUrl: './camera-event-fetch-action.component.scss'
})
export class CameraEventFetchActionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() camera!: CameraDto;
  @Input() parkingSpaceId: string | null = null;
  @Output() fetchCompleted = new EventEmitter<CameraEventFetchDto>();

  form!: FormGroup;
  masterControllers = signal<MasterDeviceAssignmentDto[]>([]);
  isLoadingControllers = signal(false);
  isSubmitting = signal(false);
  currentFetch = signal<CameraEventFetchDto | null>(null);
  readonly FetchStatus = CameraEventFetchStatus;

  private fb = inject(FormBuilder);
  private cameraEventService = inject(CameraEventService);
  private masterDeviceAssignmentService = inject(MasterDeviceAssignmentService);
  private snackBar = inject(MatSnackBar);

  private stopPolling$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initForm();
    if (this.parkingSpaceId) {
      this.loadMasterControllers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parkingSpaceId'] && !changes['parkingSpaceId'].firstChange) {
      this.loadMasterControllers();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    this.form = this.fb.group({
      masterControllerId: [null, Validators.required],
      startDate: [yesterday, Validators.required],
      startTime: ['00:00', Validators.required],
      endDate: [today, Validators.required],
      endTime: ['23:59', Validators.required]
    });
  }

  async loadMasterControllers(): Promise<void> {
    if (!this.parkingSpaceId) return;
    this.isLoadingControllers.set(true);
    try {
      const result = await firstValueFrom(
        this.masterDeviceAssignmentService.masterDeviceAssignmentsByParkingSpaceParkingSpaceIdGet(
          this.parkingSpaceId,
          false
        )
      );
      this.masterControllers.set(Array.isArray(result) ? result : []);
    } catch {
      this.masterControllers.set([]);
    } finally {
      this.isLoadingControllers.set(false);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || !this.camera?.id) return;

    const { masterControllerId, startDate, startTime, endDate, endTime } = this.form.getRawValue();

    const startDateTime = this.buildDateTime(startDate, startTime);
    const endDateTime = this.buildDateTime(endDate, endTime);

    if (endDateTime <= startDateTime) {
      this.snackBar.open('End date/time must be after start date/time.', 'Close', { duration: 4000 });
      return;
    }

    const request: FetchCameraEventsRequest = {
      cameraId: this.camera.id!,
      masterControllerId,
      timeRangeStart: Math.floor(startDateTime.getTime() / 1000),
      timeRangeEnd: Math.floor(endDateTime.getTime() / 1000)
    };

    this.isSubmitting.set(true);
    this.stopPolling$.next(); // cancel any in-progress poll
    this.currentFetch.set(null);

    this.cameraEventService.cameraEventsFetchPost(request).subscribe({
      next: (fetch: CameraEventFetchDto) => {
        this.currentFetch.set(fetch);
        this.isSubmitting.set(false);
        this.pollFetchStatus(fetch.id!);
      },
      error: () => {
        this.snackBar.open('Failed to start camera event fetch.', 'Close', { duration: 4000 });
        this.isSubmitting.set(false);
      }
    });
  }

  private pollFetchStatus(fetchId: string): void {
    interval(3000).pipe(
      startWith(0),
      switchMap(() => this.cameraEventService.cameraEventsFetchIdGet(fetchId)),
      takeWhile(result => result.status === CameraEventFetchStatus.Pending, true),
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.currentFetch.set(result);
        if (result.status === CameraEventFetchStatus.Completed) {
          this.fetchCompleted.emit(result);
        } else if (result.status === CameraEventFetchStatus.Failed) {
          this.snackBar.open(
            `Fetch failed: ${result.errorMessage || 'Unknown error'}`,
            'Close',
            { duration: 6000 }
          );
        }
      },
      error: () => {
        this.snackBar.open('Error polling fetch status.', 'Close', { duration: 4000 });
      }
    });
  }

  getMasterControllerLabel(mda: MasterDeviceAssignmentDto): string {
    return mda.masterDevice?.deviceName || mda.masterDeviceId || 'Unknown Controller';
  }

  private buildDateTime(date: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const dt = new Date(date);
    dt.setHours(hours, minutes, 0, 0);
    return dt;
  }

  get isPending(): boolean {
    return this.currentFetch()?.status === CameraEventFetchStatus.Pending;
  }

  get isCompleted(): boolean {
    return this.currentFetch()?.status === CameraEventFetchStatus.Completed;
  }

  get isFailed(): boolean {
    return this.currentFetch()?.status === CameraEventFetchStatus.Failed;
  }
}
